import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";
import { Table, Card, Row, Col, Button, InputGroup, FormControl, Badge, Form } from 'react-bootstrap';
import reportService from "../services/report.service";
import { FaChevronDown, FaChevronUp, FaSort, FaSortUp, FaSortDown, FaServer, FaSearch, FaNetworkWired } from 'react-icons/fa';

const WorkstationVersion = () => {
    const { user: currentUser } = useSelector((state) => state.auth);
    
    // State variables
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchInitiated, setSearchInitiated] = useState(false);
    const [searchName, setSearchName] = useState("");
    
    // Pagination
    const [page, setPage] = useState(0);
    const pageSize = 50;

    // Sorting states
    const [orderBy, setOrderBy] = useState("clientName");
    const [sortDirection, setSortDirection] = useState("ASC");
    
    // Nested workstation sorting states
    const [wsSortField, setWsSortField] = useState("lastSeen");
    const [wsSortDirection, setWsSortDirection] = useState("DESC");

    // Expanded rows
    const [expandedRows, setExpandedRows] = useState({});

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    if (!currentUser.roles.includes("ROLE_ADMIN")) {
        return (
            <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
                <div className="alert alert-danger">
                    <h4>Unauthorized Access</h4>
                    <p>You must be an administrator to view this page.</p>
                </div>
            </div>
        );
    }

    const fetchWorkstationVersions = (query = null) => {
        setLoading(true);
        setSearchInitiated(true);
        setPage(0);
        setExpandedRows({});
        
        reportService.getWorkstationVersions(query).then(
            (response) => {
                setData(response.data || []);
                setLoading(false);
            },
            (error) => {
                const errorMessage = (error.response && error.response.data) || error.message || error.toString();
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to fetch workstation versions: " + errorMessage });
                setLoading(false);
            }
        );
    };

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        fetchWorkstationVersions(searchName);
    };

    const handleShowAll = () => {
        setSearchName("");
        fetchWorkstationVersions(null);
    };

    const handleReset = () => {
        setSearchName("");
        setData([]);
        setSearchInitiated(false);
        setPage(0);
        setExpandedRows({});
    };

    const toggleRow = (clientId) => {
        setExpandedRows(prev => ({
            ...prev,
            [clientId]: !prev[clientId]
        }));
    };

    const handleSort = (field) => {
        if (orderBy === field) {
            setSortDirection(prev => prev === "ASC" ? "DESC" : "ASC");
        } else {
            setOrderBy(field);
            setSortDirection("ASC");
        }
        setPage(0);
    };

    const handleWsSort = (field) => {
        if (wsSortField === field) {
            setWsSortDirection(prev => prev === "ASC" ? "DESC" : "ASC");
        } else {
            setWsSortField(field);
            setWsSortDirection("ASC");
        }
    };

    const renderSortIcon = (field) => {
        if (orderBy !== field) {
            return <FaSort className="ms-1 text-white-50" style={{ opacity: 0.6 }} />;
        }
        return sortDirection === "ASC" 
            ? <FaSortUp className="ms-1 text-warning" />
            : <FaSortDown className="ms-1 text-warning" />;
    };

    const renderWsSortIcon = (field) => {
        if (wsSortField !== field) {
            return <FaSort className="ms-1 text-white-50" style={{ opacity: 0.6 }} />;
        }
        return wsSortDirection === "ASC" 
            ? <FaSortUp className="ms-1 text-warning" />
            : <FaSortDown className="ms-1 text-warning" />;
    };

    const getSortedClients = () => {
        if (!data) return [];
        return [...data].sort((a, b) => {
            let valA, valB;
            if (orderBy === "workstationCount") {
                valA = a.workstations ? a.workstations.length : 0;
                valB = b.workstations ? b.workstations.length : 0;
                return sortDirection === "ASC" ? valA - valB : valB - valA;
            } else {
                valA = a[orderBy] || "";
                valB = b[orderBy] || "";
                const strA = String(valA).trim().toLowerCase();
                const strB = String(valB).trim().toLowerCase();
                if (strA < strB) return sortDirection === "ASC" ? -1 : 1;
                if (strA > strB) return sortDirection === "ASC" ? 1 : -1;
                return 0;
            }
        });
    };

    const getSortedWorkstations = (workstations) => {
        if (!workstations) return [];
        return [...workstations].sort((a, b) => {
            let valA = a[wsSortField];
            let valB = b[wsSortField];
            
            if (valA === undefined || valA === null) valA = "";
            if (valB === undefined || valB === null) valB = "";

            if (wsSortField === "lastSeen") {
                const timeA = valA ? new Date(valA).getTime() : 0;
                const timeB = valB ? new Date(valB).getTime() : 0;
                return wsSortDirection === "ASC" ? timeA - timeB : timeB - timeA;
            } else {
                const strA = String(valA).toLowerCase();
                const strB = String(valB).toLowerCase();
                if (strA < strB) return wsSortDirection === "ASC" ? -1 : 1;
                if (strA > strB) return wsSortDirection === "ASC" ? 1 : -1;
                return 0;
            }
        });
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return "Never";
        try {
            const d = new Date(dateStr);
            return d.toLocaleString();
        } catch (e) {
            return dateStr;
        }
    };

    const handleNextPage = () => {
        const totalPages = Math.ceil(totalCount / pageSize);
        if (page + 1 < totalPages) {
            setPage(prev => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 0) {
            setPage(prev => prev - 1);
        }
    };

    const sortedClients = getSortedClients();
    const totalCount = sortedClients.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const paginatedClients = sortedClients.slice(page * pageSize, (page + 1) * pageSize);

    return (
        <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
            <style>{`
                .sortable-ws-header {
                    transition: color 0.15s ease-in-out;
                }
                .sortable-ws-header:hover {
                    color: #ffc107 !important; /* bootstrap warning yellow */
                }
            `}</style>
            <Card className="shadow-sm border-0" style={{ borderRadius: '12px' }}>
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center py-3" style={{ borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                    <div className="d-flex align-items-center">
                        <FaNetworkWired size={22} className="me-2 text-warning" />
                        <h4 className="mb-0 fw-semibold">Workstation Versions</h4>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    
                    {/* Search Panel */}
                    <Form onSubmit={handleSearch} className="mb-4">
                        <Row className="g-3 align-items-end">
                            <Col lg={7} md={6}>
                                <InputGroup>
                                    <InputGroup.Text className="bg-white border-end-0">
                                        <FaSearch className="text-muted" />
                                    </InputGroup.Text>
                                    <FormControl 
                                        placeholder="Filter by client name or client ID..."
                                        value={searchName}
                                        onChange={(e) => setSearchName(e.target.value)}
                                        disabled={loading}
                                        className="border-start-0 ps-0"
                                    />
                                    {searchName && (
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => setSearchName("")}
                                            disabled={loading}
                                        >
                                            ✕
                                        </Button>
                                    )}
                                </InputGroup>
                            </Col>

                            <Col lg={5} md={6} className="d-flex justify-content-lg-end justify-content-start mt-3 mt-md-0">
                                <Button 
                                    type="submit" 
                                    variant="primary" 
                                    disabled={loading}
                                    className="me-2 px-3 fw-semibold"
                                >
                                    Search
                                </Button>
                                <Button 
                                    variant="outline-primary" 
                                    onClick={handleShowAll}
                                    disabled={loading}
                                    className="me-2 px-3 fw-semibold"
                                >
                                    Show All Clients
                                </Button>
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={handleReset}
                                    disabled={loading}
                                    className="px-3 fw-semibold"
                                >
                                    Reset
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    {/* Mini Pagination */}
                    {searchInitiated && !loading && data.length > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mb-3 pt-3 border-top">
                            <span className="text-muted fs-6">
                                Showing <strong>{paginatedClients.length}</strong> of <strong>{totalCount}</strong> clients
                            </span>
                            <div className="d-flex align-items-center">
                                <span className="me-3 text-muted fs-6">
                                    Page <strong>{page + 1}</strong> of {Math.max(1, totalPages)}
                                </span>
                                <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="me-2 px-3 fw-semibold" 
                                    onClick={handlePrevPage} 
                                    disabled={page === 0}
                                >
                                    Previous
                                </Button>
                                <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="px-3 fw-semibold" 
                                    onClick={handleNextPage} 
                                    disabled={page + 1 >= totalPages}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Master Table */}
                    <div className="table-responsive border rounded-3 overflow-hidden">
                        <Table hover className="mb-0 align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th style={{ width: '40px' }}></th>
                                    <th 
                                        onClick={() => handleSort("clientName")} 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        className="py-3 px-4 fw-semibold text-white sortable-ws-header"
                                    >
                                        Client Name / ID {renderSortIcon("clientName")}
                                    </th>
                                    <th 
                                        onClick={() => handleSort("workstationCount")} 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        className="py-3 px-3 fw-semibold text-white text-center sortable-ws-header"
                                    >
                                        Active Workstations {renderSortIcon("workstationCount")}
                                    </th>
                                    <th className="py-3 px-4 fw-semibold text-white text-end">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!searchInitiated ? (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5 text-muted fw-semibold">
                                            <FaServer size={42} className="mb-3 text-secondary d-block mx-auto" />
                                            <h5 className="text-dark fw-bold">Ready to Load Workstation Versions</h5>
                                            <p className="mb-0 text-muted fs-6">Enter a filter above or click <strong>Show All Clients</strong> to audit workstation versions.</p>
                                        </td>
                                    </tr>
                                ) : loading ? (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5">
                                            <div className="spinner-border text-primary me-2" role="status"></div>
                                            <span className="text-muted fw-semibold fs-5">Auditing client workstations...</span>
                                        </td>
                                    </tr>
                                ) : paginatedClients.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5 text-muted fw-semibold">
                                            No workstations recorded for the selected search filter.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedClients.map((client) => {
                                        const isExpanded = !!expandedRows[client.clientId];
                                        const wsCount = client.workstations ? client.workstations.length : 0;
                                        return (
                                            <React.Fragment key={client.clientId}>
                                                <tr 
                                                    onClick={() => toggleRow(client.clientId)} 
                                                    style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                                                    className={isExpanded ? "table-active" : ""}
                                                >
                                                    <td className="text-center">
                                                        {isExpanded 
                                                            ? <FaChevronUp className="text-primary" /> 
                                                            : <FaChevronDown className="text-muted" />
                                                        }
                                                    </td>
                                                    <td className="px-4 fw-semibold text-dark fs-5">
                                                        <div>{client.clientName}</div>
                                                        <small className="text-muted monospace fw-normal" style={{ fontSize: '0.78rem', marginTop: '2px', display: 'block' }}>
                                                            ID: {client.clientId}
                                                        </small>
                                                    </td>
                                                    <td className="px-3 text-center">
                                                        <Badge bg={wsCount > 0 ? "primary" : "light"} className={wsCount > 0 ? "px-3 py-2 fs-6 text-white" : "px-3 py-2 fs-6 text-dark border"}>
                                                            {wsCount}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-4 text-end">
                                                        <Button 
                                                            variant={isExpanded ? "primary" : "outline-secondary"}
                                                            size="sm"
                                                            className="fw-semibold px-3"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleRow(client.clientId);
                                                            }}
                                                        >
                                                            {isExpanded ? "Hide" : "Show Workstations"}
                                                        </Button>
                                                    </td>
                                                </tr>

                                                {/* Expanded Details Row */}
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="4" className="bg-light p-4 border-top">
                                                            <div className="ps-4 pe-2 py-2">
                                                                 <h5 className="mb-3 text-dark fw-semibold d-flex align-items-center">
                                                                    <FaServer size={16} className="me-2 text-primary" />
                                                                    Workstation Status Detail
                                                                 </h5>
                                                                
                                                                 {wsCount === 0 ? (
                                                                     <div className="alert alert-warning border-0 mb-0 py-3 rounded-3 shadow-xs">
                                                                         No active workstation logs recorded.
                                                                     </div>
                                                                 ) : (
                                                                     <div className="table-responsive border rounded-3 overflow-hidden shadow-sm bg-white">
                                                                         <Table hover size="sm" className="mb-0 align-middle">
                                                                             <thead className="table-dark">
                                                                                 <tr>
                                                                                     <th 
                                                                                         onClick={() => handleWsSort("workstationName")} 
                                                                                         style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                                         className="py-2 px-3 fw-medium text-white sortable-ws-header"
                                                                                     >
                                                                                         Workstation Name {renderWsSortIcon("workstationName")}
                                                                                     </th>
                                                                                     <th 
                                                                                         onClick={() => handleWsSort("workstationVersion")} 
                                                                                         style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                                         className="py-2 px-3 fw-medium text-white sortable-ws-header text-center"
                                                                                     >
                                                                                         Software Version {renderWsSortIcon("workstationVersion")}
                                                                                     </th>
                                                                                     <th 
                                                                                         onClick={() => handleWsSort("lastSeen")} 
                                                                                         style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                                         className="py-2 px-3 fw-medium text-white sortable-ws-header text-center"
                                                                                     >
                                                                                         Last Seen / Activity {renderWsSortIcon("lastSeen")}
                                                                                     </th>
                                                                                 </tr>
                                                                             </thead>
                                                                             <tbody>
                                                                                 {getSortedWorkstations(client.workstations).map((ws, idx) => (
                                                                                     <tr key={idx}>
                                                                                         <td className="px-3 py-3 fw-semibold text-dark">
                                                                                            <span className="d-flex align-items-center">
                                                                                                <FaServer className="text-secondary me-2" size={12} />
                                                                                                {ws.workstationName}
                                                                                            </span>
                                                                                         </td>
                                                                                         <td className="px-3 py-3 text-center">
                                                                                             {ws.workstationVersion ? (
                                                                                                 <Badge bg="success" className="px-3 py-2 fs-6">
                                                                                                     {ws.workstationVersion}
                                                                                                 </Badge>
                                                                                             ) : (
                                                                                                 <span className="text-muted fs-6">Unknown</span>
                                                                                             )}
                                                                                         </td>
                                                                                         <td className="px-3 py-3 text-muted monospace text-center fs-6">
                                                                                             {formatDateTime(ws.lastSeen)}
                                                                                         </td>
                                                                                     </tr>
                                                                                 ))}
                                                                             </tbody>
                                                                         </Table>
                                                                     </div>
                                                                 )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination footer */}
                    {searchInitiated && !loading && totalCount > 0 && (
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-4">
                            <span className="text-muted mb-3 mb-sm-0">
                                Showing <strong>{paginatedClients.length}</strong> of <strong>{totalCount}</strong> clients
                            </span>
                            {totalPages > 1 && (
                                <div className="d-flex align-items-center">
                                    <Button 
                                        variant="outline-secondary" 
                                        className="me-2 fw-semibold"
                                        onClick={() => setPage(0)}
                                        disabled={page === 0}
                                    >
                                        First
                                    </Button>
                                    <Button 
                                        variant="outline-secondary" 
                                        className="me-2 fw-semibold"
                                        onClick={handlePrevPage} 
                                        disabled={page === 0}
                                    >
                                        Prev
                                    </Button>
                                    <span className="mx-2 font-weight-bold">
                                        Page <strong>{page + 1}</strong> of {totalPages}
                                    </span>
                                    <Button 
                                        variant="outline-secondary" 
                                        className="ms-2 me-2 fw-semibold"
                                        onClick={handleNextPage} 
                                        disabled={page + 1 >= totalPages}
                                    >
                                        Next
                                    </Button>
                                    <Button 
                                        variant="outline-secondary" 
                                        className="fw-semibold"
                                        onClick={() => setPage(totalPages - 1)}
                                        disabled={page + 1 >= totalPages}
                                    >
                                        Last
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                </Card.Body>
            </Card>
        </div>
    );
};

export default WorkstationVersion;
